import { mapWebLangToServerLang } from "@/lib/i18n/utils"
import type { CMSLinkData, Translatable } from "./types"

// 获取翻译文本
export function getTranslatedText(
	currentLocale: string,
	translatable?: Translatable | string,
	fallbackLocale: string = "zh-hant",
): string {
	if (typeof translatable === "string") {
		return translatable
	}
	const adminLocale = mapWebLangToServerLang(currentLocale)
	// 按优先级返回：当前语言 -> 英文 -> 空字符串
	if (typeof translatable === "object" && translatable !== null) {
		return (
			translatable[adminLocale] ||
			translatable[fallbackLocale] ||
			translatable["en-us"] ||
			""
		)
	}

	return "" // 如果 translatable 是 undefined 或 null，返回空字符串
}

/**
 * 获取翻译数组
 * @param currentLocale 当前语言
 * @param translatable 可翻译的数组对象
 * @param fallbackLocale 备用语言
 * @returns 翻译后的数组
 */
export function getTranslatedArray(
	currentLocale: string,
	translatable?: Record<string, string[]>,
	fallbackLocale: string = "zh-hans",
): string[] {
	if (!translatable) return []
	const adminLocale = mapWebLangToServerLang(currentLocale)
	return translatable[adminLocale] ?? translatable[fallbackLocale] ?? []
}

/**
 * 确保内部URL以"/"开头 (服务器端版本)
 * @param url 内部URL
 * @returns 规范化的内部URL
 */
export function normalizeInternalUrl(url?: string): string {
	if (!url) return "/"
	return url.startsWith("/") ? url : `/${url}`
}

/**
 * 验证外部URL是否有效 (服务器端版本)
 * @param url 外部URL
 * @returns 如果URL有效则返回true，否则返回false
 */
export function validateExternalUrl(url?: string): boolean {
	if (!url) return false
	try {
		new URL(url)
		return true
	} catch (_e) {
		console.warn("Invalid external URL:", url)
		return false
	}
}

/**
 * 获取集合页面的URL (服务器端版本)
 * @param collectionId 集合ID
 * @returns 集合页面的内部链接URL或undefined
 */
export function getCollectionUrl(collectionId?: string): string | undefined {
	if (!collectionId) return undefined
	return `/collections/${collectionId}`
}

/**
 * 根据链接类型和数据获取URL (服务器端版本)
 * @param link 链接数据对象
 * @returns 链接URL或undefined
 */
export function getLinkUrl(link?: CMSLinkData): string | undefined {
	if (!link) return undefined

	switch (link.type) {
		case "internal":
			return link.internalUrl
				? normalizeInternalUrl(link.internalUrl)
				: undefined
		case "collection":
			return link.collectionId ? getCollectionUrl(link.collectionId) : undefined
		case "external":
			return validateExternalUrl(link.externalUrl)
				? link.externalUrl
				: undefined
		case "none":
		default:
			return undefined
	}
}
